/* eslint-disable react/jsx-no-target-blank */ /* NTCONT-737 customer request. */
import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getDataFromApi, ROOT } from '../../../lib/data-service';
import gtmEvent from '../../../lib/gtmEvents'
import { enforceProtocol, formatDistance } from '../../../lib/utils';

import { useConfig } from '../../../hooks/useConfig';

import Address from './Address';
import PopoverButton from '../../PopoverButton';

const extraDealerFields = ['footerBanner'];

function Dealer({ dealer, userLocation, isFromUrl, onActiveDealerChange }) {
  const { t } = useTranslation('dealers');
  const { config } = useConfig();
  const [dealerExtra, setDealerExtra] = useState(null);
  const footerBanner = dealer.footerBanner || dealerExtra?.footerBanner || null;

  // Fetch extra data for single dealer
  useEffect(() => {
    gtmEvent({
      'event': 'view_dealer',
      'dealer_name': dealer?.company_name
    });

    let fetchFromApi = false;
    const extraFromDealer = {};
    extraDealerFields.forEach(field => {
      extraFromDealer[field] = dealer[field];

      if (typeof extraFromDealer[field] === 'undefined') {
        fetchFromApi = true;
      }
    });

    if (!fetchFromApi) {
      setDealerExtra(extraFromDealer);
      return;
    }

    getDataFromApi(`dealers/data/${dealer.slug}/`).then(response => {
      const singleDealer = response.body?.result?.dealer;
      if (singleDealer) {
        const extra = {};
        extraDealerFields.forEach(field => {
          extra[field] = singleDealer[field] || dealer[field] || null;
        });
        setDealerExtra(extra);
        console.log({ dealer, singleDealer, extra });
      }
    });
  }, [dealer]);

  // Street view
  let streetViewUrl = false;

  if (!dealer.noStreetView) {
    const streetviewUrlParts = [
      'https://maps.googleapis.com/maps/api/streetview?size=640x300',
      `key=${window.ntGKey || 'AIzaSyDaA6JQZgAw6obo_lZMDR1ok60FSNKunkQ'}`,
      '&location='
    ].join('&');
    const location = [
      dealer.streetaddress,
      dealer.zipcode,
      dealer.city,
      dealer.state
    ].join(', ');

    streetViewUrl = streetviewUrlParts + encodeURIComponent(location);
    console.log(streetViewUrl)
  }

  if (dealer.streetview_override) {
    streetViewUrl = dealer.streetview_override;
  }

  // Google Directions
  let directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.long}`;

  if (userLocation) {
    directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${dealer.lat},${dealer.long}`;
  }

  let distance = formatDistance(dealer.distance, config.distanceUnit);
  let distanceText = t('Distance');

  // Use route length if route is available
  const directions = {};
  if (directions?.routes?.length > 0) {
    const routeLength = directions.routes.reduce((value, route) => {
      return value + route.legs.reduce((legValue, leg) => {
        return legValue + leg.distance.value;
      }, 0);
    }, 0);

    if (routeLength > 0) {
      distance = formatDistance(routeLength / 1000, config.distanceUnit);
      distanceText = t('Route length');
    }
  }

  //NTCONT-976 Japan wants different address formatting
  const isJp = document.body.dataset.language === 'jp_JP';

  const dealerTitle = (
    <>
      <span itemProp="name">
        {config.reorderCompanyName && dealer.nameReorderable
          ?
          config.nameFields.map(field => dealer[field]).reverse().join(' ')
          :
          config.nameFields.map(field => dealer[field]).join(' ')
        }
      </span>
      {!isJp ? config.addressFields
        .filter(field => dealer[field] !== '')
        .map((field, fieldIndex) => {
          if (field in dealer && dealer[field] !== '') {
            return (
              <React.Fragment key={'dealer-field-' + field}>
                {', '}
                <span
                  className={field === 'streetAddress' ? 'inlineblock' : ''}
                >
                  {dealer[field]}
                </span>
              </React.Fragment>
            );
          }

          return null;
        }) : null}
    </>
  );

  const isHeavy = config.isHeavy;
  const isNad = !!dealer.company_types.find(ct => ct.type === 'nokian');
  const heavyProductGroups = !isHeavy || !dealer.heavy_product_groups || dealer.heavy_product_groups.length === 0 ? false : dealer.heavy_product_groups.map(group => (
    <li key={group.link}><a href={group.link}>{group.name}</a></li>
  ));

  const companyTypeIcons = dealer.company_types.filter(ct => !!ct.icon && ct.description).map(ct => (
    <PopoverButton key={'ct-' + ct.id} icon={ct.icon} description={ct.description}></PopoverButton>
  ));

  const phoneNumbers = !dealer.phoneNumbers ? dealer.telephone : dealer.phoneNumbers.map((number, i) => {
    const link = !number.value ? number.label : <a
      className="link"
      target="_blank" // eslint-disable-line
      rel="noreferrer noopener nofollow"
      href={'tel:' + number.value}
      itemProp="telephone"
    >
      {number.label}
    </a>;

    return <span key={i}>
      {i > 0 ? ', ' : ''}
      {link}
    </span>;
  });

  function handleClose(e) {
    e.preventDefault();
    onActiveDealerChange(null);

    gtmEvent({
      'event': 'view_dealer_list',
      'dealer_list_open_method': 'Back to dealers list'
    });
  }

  return (
    <div className="single-dealer" itemScope={true} itemType="http://schema.org/LocalBusiness">
      {streetViewUrl && (
        <div
          className="streetview"
          style={{ backgroundImage: `url(${streetViewUrl})` }}
        ></div>
      )}
      <a href={ROOT} className="back-to-list link-wrapper" data-back-to-list onClick={handleClose}>
        &lsaquo;&nbsp;
        <span className="link">{t('Back to dealers list')}</span>
      </a>
      <div className="details">
        {isFromUrl ? (
          <h1>{dealerTitle}</h1> // Use H1 if we're coming with a direct url
        ) : (
          <h2>{dealerTitle}</h2>
        )}

        {isNad && (
          <h4>{t('Nokian tyres authorized dealer')}</h4>
        )}

        {companyTypeIcons.length > 0 && (
          <div className="company-types">{companyTypeIcons}</div>
        )}

        {distance && userLocation && (
          <p>
            {distanceText} {distance} {t('du')}
          </p>
        )}

        {directionsUrl && (
          <p>
            <a
              itemProp="url"
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={directionsUrl}
              className="button button--primary"
            >
              {t('Show driving directions')}
            </a>
          </p>
        )}

        {heavyProductGroups && (
          <>
            <h2 className="h3">{t('Selling products in categories')}</h2>
            <ul className="product-groups">{heavyProductGroups}</ul>
          </>
        )}

        <h2 className="h3">{t('Address')}</h2>
        <p
          itemProp="address"
          itemScope
          itemType="http://schema.org/PostalAddress"
        >
          <Address {...dealer} />
        </p>

        {phoneNumbers && (
          <>
            <h2 className="h3">{t('Tel')}</h2>
            <p>
              {phoneNumbers}
            </p>
          </>
        )}

        {dealer.website_url && (
          <>
            <h2 className="h3">{t('Website')}</h2>
            <p>
              <a
                className="link"
                target="_blank" // eslint-disable-line
                rel="noopener" // eslint-disable-line
                href={enforceProtocol(dealer.website_url)}
                itemProp="url"
              >
                {dealer.website_url}
              </a>
            </p>
          </>
        )}

        {/**dealer.contact_email && (
          <>
            <h2 className="h3">{t('Email')}</h2>
            <p>
            <a
              className="link"
              target="_blank" // eslint-disable-line
              rel="noreferrer noopener nofollow"
              href={'mailto:' + dealer.contact_email}
              itemProp="email"
            >
              {dealer.contact_email}
            </a>
            </p>
          </>
        ) '*/}

        {(config.includeServices || config.showServices && dealer.services_data.length > 0) && (
          <>
            <h2 className="h3">{t('Services')}</h2>
            <ul className="dealer-services-list">
              {dealer.services_data.map(service => (
                <li key={service.id}>{service.name}</li>
              ))}
            </ul>
          </>
        )}

        {dealer.description && (
          <>
            <h2 className="h3">{t('About')}</h2>
            <div className="dealer-description" dangerouslySetInnerHTML={{ __html: dealer.description }}></div>
          </>
        )}

        {footerBanner && (
          <div className="dealer-footer" dangerouslySetInnerHTML={{ __html: footerBanner }}></div>
        )}
      </div>
    </div>
  )
}

Dealer.propTypes = {
  dealer: propTypes.object,
  userLocation: propTypes.object,
  isFromUrl: propTypes.bool,
  onActiveDealerChange: propTypes.func,
};

export default Dealer;
